import React, { useState, useEffect } from 'react';
import { setBreadcrumbs } from 'works/legacy/lib/client-store';
import { PageSteps } from '@common/components';
import { Form } from 'antd';
import { RATES_URLS } from '@institutions/services';
import { useNavigate, useMutation, useQuery } from '@common/hooks';
import { ORGANIZATION_ADMIN_MENU } from 'works/legacy/pages/organization/admin-settings/organization-admin-vertical-nav-menu';
import { IncentivesUpdateSelectWorkforceGroupsStep } from './_incentives-update-select-workforce-groups-step';
import { IncentivesUpdateSelectUnitsStep } from './_incentives-update-select-units-step';
import { IncentivesUpdateConfirmationStep } from './_incentives-update-confirmation-step';
import { IncentivesUpdateStepsErrorModal } from './_incentives-update-steps-error-modal';
import { IncentivesUpdateRatesSetupStep } from './_incentives-update-rates-setup-step';
import { getTransformedData } from './_incentives-update-steps-service';
import { createMessage } from 'works/legacy/lib/message-helper';
import {
  InsertWorkforceGroupUnitRatesMutation,
  InsertWorkforceGroupUnitRatesMutationArgs,
  InsertWorkforceGroupUnitRatesMutationResponse,
  SelectOrganizationUnitType,
} from '@institutions/graphql';
import {
  UnitRateType,
  SimplifiedWorkforceGroupType,
  WorkforceGroupById,
} from '@scheduling/graphql';
import { useQueryParam } from 'works/legacy/services/query-params-service';

export function IncentivesUpdateSteps() {
  const workforceGroupId = useQueryParam('workforceGroupId');

  const [insertWorkforceGroupUnitRates] = useMutation<
    InsertWorkforceGroupUnitRatesMutationResponse,
    InsertWorkforceGroupUnitRatesMutationArgs
  >(InsertWorkforceGroupUnitRatesMutation);

  const { data: workforceGroupData } = useQuery(WorkforceGroupById, {
    variables: { workforceGroupId: workforceGroupId || '' },
    skip: !workforceGroupId,
  });

  useEffect(() => {
    if (workforceGroupId && workforceGroupData?.workforceGroup) {
      setBreadcrumbs([
        {
          name: 'Admin',
          path: '/works/admin',
        },
        {
          name: 'Groups',
          path: ORGANIZATION_ADMIN_MENU.GROUPS.ROUTE,
        },
        {
          name: workforceGroupData.workforceGroup.name,
          path: `${ORGANIZATION_ADMIN_MENU.GROUPS.ROUTE}/${workforceGroupId}/details`,
        },
        {
          name: 'Update Rates',
          path: `${RATES_URLS.INCENTIVES}/update?workforceGroupId=${workforceGroupId}`,
        },
      ]);
    } else {
      setBreadcrumbs([
        {
          name: 'Admin',
          path: '/works/admin',
        },
        {
          name: 'Rates',
          path: RATES_URLS.BASE,
        },
        {
          name: 'Incentives',
          path: RATES_URLS.INCENTIVES,
        },
        {
          name: 'Update Incentives',
          path: `${RATES_URLS.INCENTIVES}/update`,
        },
      ]);
    }
  }, [workforceGroupData?.workforceGroup, workforceGroupId]);

  const [selectedWorkforceGroups, setSelectedWorkforceGroups] = useState<
    SimplifiedWorkforceGroupType[]
  >([]);
  const [effectiveDate, setEffectiveDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedOrgsUnits, setSelectedOrgsUnits] = useState<
    SelectOrganizationUnitType[]
  >([]);
  const [ratesValues, setRatesValues] = useState({} as UnitRateType);

  const [stepThreeForm] = Form.useForm();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const effectivePricingMode =
    selectedOrgsUnits[0]?.pricingMode === 'STATIC' ? 'STATIC' : 'DYNAMIC';

  useEffect(() => {
    if (
      workforceGroupData?.workforceGroup &&
      selectedWorkforceGroups.length === 0
    ) {
      const simplifiedWorkforceGroup: SimplifiedWorkforceGroupType = {
        id: workforceGroupData.workforceGroup.id,
        name: workforceGroupData.workforceGroup.name,
        hideHomeUnitName: workforceGroupData.workforceGroup.hideHomeUnitName,
        hideFloatUnitName: workforceGroupData.workforceGroup.hideFloatUnitName,
      };
      setSelectedWorkforceGroups([simplifiedWorkforceGroup]);
    }
  }, [selectedWorkforceGroups.length, workforceGroupData]);

  function goToRatesOnDemandMainPage() {
    navigate(RATES_URLS.INCENTIVES);
  }

  async function onSave() {
    setLoading(true);
    setHasError(false);

    const transformedData = getTransformedData({
      selectedWorkforceGroups,
      effectiveDate,
      endDate,
      selectedOrgsUnits,
      ratesValues,
      pricingMode: effectivePricingMode,
    });

    const { data, errors } = await insertWorkforceGroupUnitRates({
      variables: transformedData,
    });

    if (errors) {
      setLoading(false);
      setHasError(true);
      return;
    }

    const success = data?.insertWorkforceGroupUnitRates.success;

    if (success) {
      setLoading(false);
      createMessage(
        'success',
        'Rate updates scheduled successfully. Large updates may take up to 30 minutes.'
      );
      goToRatesOnDemandMainPage();
    }
  }

  return (
    <>
      <IncentivesUpdateStepsErrorModal onSave={onSave} visible={hasError} />
      <PageSteps
        steps={[
          {
            title: 'Select Workforce Groups',
            page: (
              <IncentivesUpdateSelectWorkforceGroupsStep
                workforceGroups={selectedWorkforceGroups}
                onWorkforceGroupsChange={setSelectedWorkforceGroups}
              />
            ),
            forwardButtonDisabled: selectedWorkforceGroups.length === 0,
          },
          {
            title: 'Select Units',
            page: (
              <IncentivesUpdateSelectUnitsStep
                orgUnits={selectedOrgsUnits}
                onOrgUnitsChange={setSelectedOrgsUnits}
              />
            ),
            forwardButtonDisabled: selectedOrgsUnits.length === 0,
          },
          {
            title: 'Select Rates',
            page: (
              <IncentivesUpdateRatesSetupStep
                form={stepThreeForm}
                setRatesValues={setRatesValues}
                effectiveDate={effectiveDate}
                setEffectiveDate={setEffectiveDate}
                setEndDate={setEndDate}
                pricingMode={effectivePricingMode}
              />
            ),
            validate: async () => {
              try {
                await stepThreeForm.validateFields();
                stepThreeForm.submit();
                return true;
              } catch {
                return false;
              }
            },
          },
          {
            title: 'Confirm Rate Updates',
            confirmButtonText: 'Confirm Updates',
            page: (
              <IncentivesUpdateConfirmationStep
                selectedOrgsUnits={selectedOrgsUnits}
                effectiveDate={effectiveDate}
                endDate={endDate}
                selectedWorkforceGroups={selectedWorkforceGroups}
                ratesValues={ratesValues}
                pricingMode={effectivePricingMode}
              />
            ),
          },
        ]}
        confirmLoading={loading}
        onCancel={goToRatesOnDemandMainPage}
        onConfirm={onSave}
      />
    </>
  );
}
